// New invoice.
import { useState } from "react";
import { useClients, usePeople } from "../../api/hooks";
import { useNewInvoice } from "../../api/money";
import { useSession } from "../../app/session";
import { Combobox } from "../../components/fields";
import { Modal } from "../../components/overlays";
import { Banner, Button } from "../../components/parts";
import { draftLine, type DraftLine, errText, LinesEditor, linesOut } from "../../components/work";

/**
 * An invoice typed by hand (question 39): a fee, a product, work done without a visit. The old Steward's form (client, lines,
 * who did the work for pay, a message) with Jobber's Save or Save and send. Saved, it waits in To check.
 */
export function NewInvoice({
  clientId: firstClient,
  onClose,
  onMade,
}: {
  clientId: string;
  onClose: () => void;
  onMade: (id: string, send: boolean) => void;
}) {
  const { company } = useSession();
  const clients = useClients(company.id, { sort: "last_activity" });
  const people = usePeople(company.id);
  const make = useNewInvoice();
  const [clientId, setClientId] = useState(firstClient);
  const [lines, setLines] = useState<DraftLine[]>(() => [draftLine()]);
  const [crew, setCrew] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const team = (people.data ?? []).filter((p) => p.active);
  const go = (send: boolean) => {
    if (!clientId) {
      setError("Pick the client.");
      return;
    }
    const out = linesOut(lines);
    if (typeof out === "string") {
      setError(out);
      return;
    }
    if (!out.length) {
      setError("Add a line with what it is for and the price.");
      return;
    }
    setSending(send);
    make.mutate(
      {
        clientId,
        lines: out.map((l) => ({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitPriceCents })),
        ...(note.trim() ? { noteToClient: note.trim() } : {}),
        ...(crew.length ? { crew } : {}),
      },
      {
        onSuccess: (d) => onMade(d.id, send),
        onError: (e) => setError(errText(e, "That did not save.")),
      },
    );
  };
  return (
    <Modal
      title="New invoice"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="secondary" loading={make.isPending && !sending} onClick={() => go(false)}>
            Save invoice
          </Button>
          <Button icon="mail" loading={make.isPending && sending} onClick={() => go(true)}>
            Save and send
          </Button>
        </>
      }
    >
      <div className="stack">
        <Combobox
          label="Client"
          value={clientId}
          onChange={setClientId}
          options={(clients.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        <LinesEditor lines={lines} onChange={setLines} />
        {team.length ? (
          <div className="field">
            <span className="label">Who did the work (for pay)</span>
            <div className="chips">
              {team.map((p) => (
                <label key={p.id} className="check-row">
                  <input
                    type="checkbox"
                    checked={crew.includes(p.id)}
                    onChange={(e) => setCrew(e.target.checked ? [...crew, p.id] : crew.filter((x) => x !== p.id))}
                  />
                  {p.name}
                </label>
              ))}
            </div>
            <span className="muted small">Leave everyone off for a fee, a product, or work that was already paid.</span>
          </div>
        ) : null}
        <label className="field">
          <span className="label">Message to the client (optional)</span>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <p className="muted small">Save invoice puts it in To check and sends nothing. Save and send shows the message first.</p>
        {error ? <Banner type="error">{error}</Banner> : null}
      </div>
    </Modal>
  );
}
